import { format } from 'date-fns'

// Backend `date` columns (transaction dates, statement periods) arrive as
// date-only strings like "2025-12-01": a calendar day with no time or timezone.
// Parsing them with `new Date("2025-12-01")` treats them as UTC midnight, which
// shifts the day backward in negative-offset timezones. These helpers keep a
// calendar date as a calendar date, parsed and formatted in local time.

const CALENDAR_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/

/**
 * Parse a calendar date into a local `Date` at midnight, without shifting the
 * day across timezones. A leading `YYYY-MM-DD` is read literally; anything else
 * (a real timestamp or a `Date`) falls back to native parsing.
 */
export const parseCalendarDate = (value: string | Date): Date => {
  if (value instanceof Date) return value

  const match = CALENDAR_DATE_PREFIX.exec(value)
  if (match) {
    const [, year, month, day] = match
    return new Date(Number(year), Number(month) - 1, Number(day))
  }

  return new Date(value)
}

/**
 * Serialize a value to the canonical `YYYY-MM-DD` calendar-date string using
 * local components. A string that already begins with `YYYY-MM-DD` keeps that
 * literal day (so legacy `…T00:00:00.000Z` filter values normalize cleanly).
 * Nullish or unparseable input yields an empty string rather than throwing.
 */
export const toCalendarDate = (value?: string | Date | null): string => {
  if (value == null) return ''
  if (typeof value === 'string' && CALENDAR_DATE_PREFIX.test(value)) {
    return value.slice(0, 10)
  }

  const parsed = parseCalendarDate(value)
  return Number.isNaN(parsed.getTime()) ? '' : format(parsed, 'yyyy-MM-dd')
}

/**
 * Format a calendar date for display, parsed in local time. Nullish or
 * unparseable input yields an empty string rather than throwing.
 */
export const formatCalendarDate = (
  value?: string | Date | null,
  formatStr = 'MMM d, yyyy',
): string => {
  if (value == null) return ''

  const parsed = parseCalendarDate(value)
  return Number.isNaN(parsed.getTime()) ? '' : format(parsed, formatStr)
}
